import { zodResolver } from '@hookform/resolvers/zod';
import { backend, domain } from 'modules/data';
import { useDispatch, useSelector } from 'modules/react-redux';
import { createXeroContact } from 'pages/requestForm/actions';
import type { XeroContext } from 'pages/requestForm/data/xero/XeroContext';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { XeroContactCreationPopupData } from '../../../reducers/page/activePopup/xeroContactCreationPopupReducer';
import { getActivePopup, getRequest } from '../../../selectors/pageSelectors';
import { getXeroContext } from '../../../selectors/xero';
import { getContactFieldNameByIntegrationCode } from './XeroCreateContactPopup.helpers';
import { schema } from './XeroCreateContactPopup.schema';
import type { FormValues } from './XeroCreateContactPopup.types';

export const useInitialData = () => {
    const xeroContext = useSelector(getXeroContext);
    const request = useSelector(getRequest);
    const company = useCompanyBasedOnUrl();
    const activePopup = useSelector<XeroContactCreationPopupData>(getActivePopup);
    const { initialContact } = activePopup;

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const { data: ocrData, isFetching: isFetchingOcrData } = useOcrData(request, ocrAttachment?.id);

    const isUKCompany = company.integration?.countryCode === backend.OrganisationVersion.UK;
    const hideBatchPaymentInContact = company.betaFeatures.includes(
        domain.CompanyBetaFeature.HideBatchPaymentInContact
    );

    const initialValues = useMemo(() => {
        const fieldName = getContactFieldNameByIntegrationCode(request.integrationCode);

        return ocrData
            ? {
                  ...initialContact,
                  name: ocrData.document?.[fieldName]?.name || initialContact.name,
                  bankAccountDetails:
                      ocrData.document?.[fieldName]?.bankAccountDetails || initialContact.bankAccountDetails,
                  taxNumber: ocrData.document?.[fieldName]?.taxNumber || initialContact.taxNumber,
                  defaultCurrency: ocrData.document?.currencyCode
                      ? {
                            id: ocrData.document?.currencyCode,
                            text:
                                xeroContext.currencies.find(
                                    (currency) => currency.id === ocrData.document?.currencyCode
                                )?.text || '',
                        }
                      : initialContact.defaultCurrency,
                  billingAddress: {
                      ...initialContact.billingAddress,
                      address: ocrData.document?.[fieldName]?.address?.street || initialContact.billingAddress.address,
                      city: ocrData.document?.[fieldName]?.address?.city || initialContact.billingAddress.city,
                      region: ocrData.document?.[fieldName]?.address?.region || initialContact.billingAddress.region,
                      country: ocrData.document?.[fieldName]?.address?.country || initialContact.billingAddress.country,
                      postalCode:
                          ocrData.document?.[fieldName]?.address?.postalCode ||
                          initialContact.billingAddress.postalCode,
                  },
              }
            : initialContact;
    }, [initialContact, ocrData, request.integrationCode, xeroContext.currencies]);

    return {
        xeroContext,
        isUKCompany,
        hideBatchPaymentInContact,
        initialValues,
        loading: isFetchingOcrData,
    };
};

export const useXeroCreateContactForm = (
    isUKCompany: boolean,
    trackingCategories: XeroContext['trackingCategories'],
    initialValues: FormValues | Record<never, never>
) => {
    const dispatch = useDispatch();
    const form = useForm<FormValues>({
        defaultValues: initialValues,
        resolver: zodResolver(schema),
    });

    const handleSubmit = form.handleSubmit(
        ({ sameAsBillingAddress, purchasesTrackingCategoryOptionIds, salesTrackingCategoryOptionIds, ...values }) => {
            const normalizeCategoryOption = (categoryOptionId: string, index: number) => ({
                id: categoryOptionId,
                name: trackingCategories[index].category.text,
                text: trackingCategories[index].options.find((option) => option.id === categoryOptionId)?.text ?? '',
            });

            if (salesTrackingCategoryOptionIds?.length) {
                values.salesTrackingCategories = salesTrackingCategoryOptionIds.map(normalizeCategoryOption);
            }

            if (purchasesTrackingCategoryOptionIds?.length) {
                values.purchasesTrackingCategories = purchasesTrackingCategoryOptionIds.map(normalizeCategoryOption);
            }

            if (sameAsBillingAddress) {
                values.deliveryAddress = values.billingAddress;
            }

            if (isUKCompany && values.bankAccount?.sortCode) {
                values.bankAccountDetails = `${values.bankAccount.sortCode}${values.bankAccount.bankAccountDetails}`;
            }

            dispatch(createXeroContact(values));
        }
    );

    return { form, handleSubmit };
};
